import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Box,
    Typography,
    Paper,
    Button,
    Tabs,
    Tab,
    CircularProgress,
    Alert,
    Breadcrumbs,
    Link,
    Divider,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Dashboard as DashboardIcon,
    Article as ContentIcon,
    AccountTree as PrerequisitesIcon,
    Quiz as QuizIcon,
    Assignment as AssessmentIcon,
    NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import AdminAssessmentDashboard from '../components/Admin/AdminAssessmentDashboard';
import SubtopicsManager from '../components/SubtopicsManager';
import TopicContentManager from '../components/Admin/TopicContentManager';
import adminApi from '../services/adminApi';
import { getCurriculumSubjectById, getTopicContent } from '../services/adminCourseService';

import AdminQuizDashboard from '../components/Admin/AdminQuizDashboard';

const TRUNCATE_LIMIT = 20;
const truncate = (str: string, max: number = TRUNCATE_LIMIT) => {
    if (!str) return '';
    return str.length > max ? str.substring(0, max) + '...' : str;
};


interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`topic-tabpanel-${index}`}
            aria-labelledby={`topic-tab-${index}`}
            {...other}
            style={{ height: '100%' }}
        >
            {value === index && (
                <Box sx={{ p: 3, height: '100%' }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `topic-tab-${index}`,
        'aria-controls': `topic-tabpanel-${index}`,
    };
}

const AdminTopicDashboard: React.FC = () => {
    const { subjectId, topicId } = useParams<{ subjectId: string; topicId: string }>();
    const navigate = useNavigate();
    const [tabValue, setTabValue] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [topic, setTopic] = useState<any | null>(null);

    const [subject, setSubject] = useState<any | null>(null);

    useEffect(() => {
        if (topicId) {
            loadTopic();
        }
    }, [topicId]);

    useEffect(() => {
        if (subjectId) {
            loadSubject();
        }
    }, [subjectId]);

    const loadSubject = async () => {
        try {
            const data = await getCurriculumSubjectById(Number(subjectId));
            setSubject(data);
        } catch (error) {
            console.error('Failed to load subject', error);
        }
    };

    const loadTopic = async () => {
        try {
            setLoading(true);
            const response = await adminApi.get(`/api/admin/graph/topics/${topicId}`);
            if (response.data && response.data.data) {
                const topicData = response.data.data;
                // Content is stored in topic_content table; load from TopicContentController
                const content = await getTopicContent(Number(topicId)).catch(() => null);
                setTopic({ ...topicData, content: content ?? topicData.content ?? '' });
            } else {
                throw new Error('Topic data not found');
            }
        } catch (err: any) {
            console.error('Error loading topic:', err);
            setError(err.response?.data?.message || err.message || 'Failed to load topic');
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    if (loading) {
        return (
            <Container maxWidth="xl" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (error || !topic) {
        return (
            <Container maxWidth="xl" sx={{ mt: 4 }}>
                <Alert severity="error">{error || 'Topic not found'}</Alert>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(`/admin/subjects/${subjectId}/topics`)}
                    sx={{ mt: 2 }}
                >
                    Back to Subject Topics
                </Button>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4, height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column' }}>
            {/* Header & Breadcrumbs */}
            <Box mb={3}>
                <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
                    <Link
                        underline="hover"
                        color="inherit"
                        href="/admin/subjects"
                        onClick={(e) => { e.preventDefault(); navigate('/admin/subjects'); }}
                    >
                        Subjects
                    </Link>
                    <Link
                        underline="hover"
                        color="inherit"
                        href={`/admin/subjects/${subjectId}/topics`}
                        onClick={(e) => { e.preventDefault(); navigate(`/admin/subjects/${subjectId}/topics`); }}
                    >
                        {subject ? `${truncate(subject.name)} (${subject.code})` : 'Topics'}
                    </Link>
                    <Typography color="text.primary">
                        {topic ? `${truncate(topic.title)} (${topic.id})` : '...'}
                    </Typography>
                </Breadcrumbs>

                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h4" component="h1" fontWeight="bold">
                        {topic.title}
                    </Typography>
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate(`/admin/subjects/${subjectId}/topics`)}
                    >
                        Back
                    </Button>
                </Box>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                    {topic.description}
                </Typography>
            </Box>

            {/* Tabs */}
            <Paper sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={tabValue} onChange={handleTabChange} aria-label="topic dashboard tabs">
                        <Tab icon={<DashboardIcon />} iconPosition="start" label="Overview" {...a11yProps(0)} />
                        <Tab icon={<ContentIcon />} iconPosition="start" label="Content" {...a11yProps(1)} />
                        {/* New Subtopics Tab */}
                        <Tab icon={<PrerequisitesIcon />} iconPosition="start" label="Child Topics" {...a11yProps(2)} />
                        <Tab icon={<PrerequisitesIcon />} iconPosition="start" label="Prerequisites" {...a11yProps(3)} />
                        <Tab icon={<QuizIcon />} iconPosition="start" label="Quizzes" {...a11yProps(4)} />
                        <Tab icon={<AssessmentIcon />} iconPosition="start" label="Assessments" {...a11yProps(5)} />
                    </Tabs>
                </Box>

                {/* Tab Panels with Scrollable Content */}
                <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                    <CustomTabPanel value={tabValue} index={0}>
                        <Typography variant="h6" gutterBottom>Overview</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Metric placeholders will go here (e.g. number of students who completed this topic, average quiz score, etc.)
                        </Typography>
                        {/* TODO: Add stats widgets here */}
                    </CustomTabPanel>

                    <CustomTabPanel value={tabValue} index={1}>
                        <TopicContentManager
                            subjectId={Number(subjectId)}
                            topic={topic}
                            onContentUpdate={(updatedTopic: any) => setTopic(updatedTopic)}
                        />
                    </CustomTabPanel>

                    <CustomTabPanel value={tabValue} index={2}>
                        <SubtopicsManager
                            subtopics={topic.children || []} // Backend updated to return children
                            topicId={Number(topicId)}
                            subjectId={Number(subjectId)}
                            parentId={Number(topicId)}
                            onRefresh={loadTopic}
                        />
                    </CustomTabPanel>



                    <CustomTabPanel value={tabValue} index={3}>
                        <Typography variant="h6" gutterBottom>Prerequisites Graph</Typography>
                        {/* TODO: Integrate Topic Graph Visualizer/Editor */}
                    </CustomTabPanel>

                    <CustomTabPanel value={tabValue} index={4}>
                        <AdminQuizDashboard topicId={Number(topicId)} />
                    </CustomTabPanel>

                    <CustomTabPanel value={tabValue} index={5}>
                        <AdminAssessmentDashboard topicId={Number(topicId)} subjectId={Number(subjectId)} />
                    </CustomTabPanel>
                </Box>
            </Paper>
        </Container>
    );
};

export default AdminTopicDashboard;
